import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Link as MuiLink,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import {
  FEEDBACK_ASPECT_LABELS,
  FEEDBACK_CATEGORY_LABELS,
  FEEDBACK_MESSAGE_MAX,
  FeedbackAspectRatings,
  FeedbackAuthRequiredError,
  submitFeedback,
  type FeedbackCategory,
  type FeedbackContextKind,
} from '@/lib/feedback';

type Props = {
  open: boolean;
  onClose: () => void;
  contextKind?: FeedbackContextKind;
  /** ID einer gespeicherten Analyse, falls das Feedback sich darauf bezieht. */
  savedAnalysisId?: string | null;
  /** Vorgabe für die Sterne-Bewertung (z. B. nach Analyse-Ende: keine Voreinstellung). */
  defaultRating?: number;
  /** Nutzer-spezifischer Untertitel im Dialog (z. B. „Wie war deine Analyse?"). */
  title?: string;
  subtitle?: string;
  /** Wird beim Erfolg vom Aufrufer behandelt (z. B. Snackbar zeigen). */
  onSubmitted?: () => void;
  /** Wenn nicht eingeloggt: Aufrufer öffnet Login-Prompt. */
  onAuthRequired?: () => void;
};

const RATING_LABELS: Record<number, string> = {
  1: 'Sehr unzufrieden',
  2: 'Unzufrieden',
  3: 'Neutral',
  4: 'Zufrieden',
  5: 'Sehr zufrieden',
};

export function FeedbackDialog({
  open,
  onClose,
  contextKind,
  savedAnalysisId = null,
  defaultRating = 0,
  title = 'Feedback geben',
  subtitle,
  onSubmitted,
  onAuthRequired,
}: Props) {
  const [rating, setRating] = useState<number>(defaultRating);
  const [hoverRating, setHoverRating] = useState<number>(0);
  const [category, setCategory] = useState<FeedbackCategory>('idea');
  const [message, setMessage] = useState('');
  const [aspectRatings, setAspectRatings] = useState<FeedbackAspectRatings>({});
  const [nps, setNps] = useState<number | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (open) {
      setRating(defaultRating);
      setHoverRating(0);
      setCategory('idea');
      setMessage('');
      setAspectRatings({});
      setNps(null);
      setShowDetails(false);
      setError(null);
      setSuccess(false);
      setSubmitting(false);
    }
  }, [open, defaultRating]);

  const canSubmit = useMemo(
    () => rating >= 1 && rating <= 5 && message.trim().length > 0 && !submitting,
    [rating, message, submitting]
  );

  const ratingForLabel = hoverRating || rating;
  const ratingLabel = ratingForLabel > 0 ? RATING_LABELS[ratingForLabel] : 'Bitte bewerte deine Erfahrung';

  async function handleSubmit() {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      await submitFeedback({
        rating,
        category,
        message: message.trim(),
        context_kind: contextKind ?? null,
        saved_analysis_id: savedAnalysisId,
        aspect_ratings: aspectRatings,
        nps,
      });
      setSuccess(true);
      onSubmitted?.();
      // Kleine Wartezeit, damit der Erfolgs-Hinweis sichtbar wird, dann schließen.
      window.setTimeout(() => onClose(), 1100);
    } catch (e) {
      if (e instanceof FeedbackAuthRequiredError) {
        onAuthRequired?.();
        onClose();
        return;
      }
      setError(e instanceof Error ? e.message : 'Feedback konnte nicht gesendet werden');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pb: 0.5 }}>{title}</DialogTitle>
      <DialogContent>
        {subtitle ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {subtitle}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Dein Feedback hilft uns, ImmoKompass besser zu machen. Wir lesen jede
            Nachricht.
          </Typography>
        )}

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Bewertung
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
            {[1, 2, 3, 4, 5].map((n) => {
              const filled = n <= (hoverRating || rating);
              return (
                <IconButton
                  key={n}
                  size="medium"
                  aria-label={`${n} von 5 Sternen`}
                  onClick={() => setRating(n)}
                  onMouseEnter={() => setHoverRating(n)}
                  onMouseLeave={() => setHoverRating(0)}
                  sx={{ p: 0.5, color: filled ? '#f5b301' : 'action.disabled' }}
                >
                  {filled ? <StarIcon /> : <StarBorderIcon />}
                </IconButton>
              );
            })}
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              {ratingLabel}
            </Typography>
          </Box>
        </Box>

        <TextField
          select
          label="Worum geht es?"
          value={category}
          onChange={(e) => setCategory(e.target.value as FeedbackCategory)}
          fullWidth
          size="small"
          sx={{ mb: 2 }}
          disabled={submitting}
        >
          {(Object.keys(FEEDBACK_CATEGORY_LABELS) as FeedbackCategory[]).map((key) => (
            <MenuItem key={key} value={key}>
              {FEEDBACK_CATEGORY_LABELS[key]}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          label="Deine Nachricht"
          placeholder="Was hat gut funktioniert? Was sollten wir verbessern?"
          value={message}
          onChange={(e) => setMessage(e.target.value.slice(0, FEEDBACK_MESSAGE_MAX))}
          multiline
          minRows={4}
          maxRows={10}
          fullWidth
          disabled={submitting}
          helperText={`${message.length} / ${FEEDBACK_MESSAGE_MAX}`}
        />

        <Box sx={{ mt: 2 }}>
          <MuiLink
            component="button"
            type="button"
            underline="hover"
            onClick={() => setShowDetails((s) => !s)}
            sx={{ fontSize: '0.85rem', fontWeight: 600 }}
          >
            {showDetails
              ? 'Detail-Bewertungen ausblenden'
              : 'Detail-Bewertungen hinzufügen (optional)'}
          </MuiLink>
        </Box>

        <Collapse in={showDetails} timeout="auto" unmountOnExit>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Wie bewertest du diese Aspekte?
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
            Optional — überspringe einfach, was nicht relevant ist.
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1.5 }}>
            {(Object.keys(FEEDBACK_ASPECT_LABELS) as (keyof FeedbackAspectRatings)[]).map(
              (key) => (
                <AspectStarsRow
                  key={key}
                  label={FEEDBACK_ASPECT_LABELS[key]}
                  value={aspectRatings[key] ?? 0}
                  onChange={(v) =>
                    setAspectRatings((prev) => {
                      const next = { ...prev };
                      if (v <= 0) delete next[key];
                      else next[key] = v;
                      return next;
                    })
                  }
                  disabled={submitting}
                />
              )
            )}
          </Box>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Würdest du ImmoKompass weiterempfehlen?
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1.25, display: 'block' }}>
            0 = unwahrscheinlich · 10 = sehr wahrscheinlich
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => {
              const selected = nps === n;
              const color =
                n <= 6 ? '#ef4444' : n <= 8 ? '#f59e0b' : '#10b981'; // detractor/passive/promoter
              return (
                <ButtonBase
                  key={n}
                  onClick={() => setNps(selected ? null : n)}
                  disabled={submitting}
                  sx={{
                    minWidth: 36,
                    height: 36,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: selected ? color : 'divider',
                    bgcolor: selected ? color : 'transparent',
                    color: selected ? '#fff' : 'text.primary',
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    transition: 'all 120ms',
                    '&:hover': { borderColor: color, bgcolor: selected ? color : 'rgba(0,0,0,0.04)' },
                  }}
                >
                  {n}
                </ButtonBase>
              );
            })}
          </Box>
          {nps != null ? (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              {nps <= 6
                ? 'Detractor — Was würdest du dir anders wünschen?'
                : nps <= 8
                  ? 'Passive — Was fehlt zu einer 9 oder 10?'
                  : 'Promoter — Vielen Dank!'}
            </Typography>
          ) : null}
        </Collapse>

        {error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        ) : null}
        {success ? (
          <Alert severity="success" sx={{ mt: 2 }}>
            Danke! Dein Feedback ist eingegangen.
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={submitting}>
          Abbrechen
        </Button>
        <Button
          onClick={() => void handleSubmit()}
          variant="contained"
          disabled={!canSubmit || success}
        >
          {submitting ? 'Sende …' : 'Feedback senden'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function AspectStarsRow({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  disabled?: boolean;
}) {
  const [hover, setHover] = useState(0);
  const display = hover || value;
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '1fr auto' },
        alignItems: 'center',
        gap: 0.5,
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {[1, 2, 3, 4, 5].map((n) => {
          const filled = n <= display;
          return (
            <IconButton
              key={n}
              size="small"
              aria-label={`${label}: ${n} von 5 Sternen`}
              onClick={() => onChange(value === n ? 0 : n)}
              onMouseEnter={() => setHover(n)}
              onMouseLeave={() => setHover(0)}
              disabled={disabled}
              sx={{ p: 0.25, color: filled ? '#f5b301' : 'action.disabled' }}
            >
              {filled ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
            </IconButton>
          );
        })}
      </Box>
    </Box>
  );
}
